/**
 * Consent Storage Module
 * Handles storing and retrieving consent data in cookies
 */

import { encodeTCString, decodeTCString } from './tc-string.js';
import { buildACString } from './ac-string.js';
import { isVendorDeleted } from './gvl-data.js';
import { deleteAllTrackingCookies, disableGoogleAnalytics, enableGoogleAnalytics } from './vendor-config.js';

// Cookie names per TCF specification
const TC_STRING_COOKIE = 'euconsent-v2';
const CONSENT_DATA_COOKIE = 'tcf_consent_data';
// Google Additional Consent string cookie
const AC_STRING_COOKIE = 'addtl_consent';

// Cookie expiration: 13 months (TCF requirement)
const COOKIE_EXPIRY_DAYS = 390;

/**
 * Set a cookie with proper attributes
 */
function setCookie(name, value, days = COOKIE_EXPIRY_DAYS) {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;
  
  // SameSite=Lax for better compatibility
  document.cookie = `${name}=${encodeURIComponent(value)};${expires};path=/;SameSite=Lax`;
}

/**
 * Get a cookie value
 */
function getCookie(name) {
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(';');
  
  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length));
    }
  }
  return null;
}

/**
 * Delete a cookie
 */
function deleteCookie(name) {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax`;
}

/**
 * Store consent data
 * @param {Object} consentData - The consent data to store
 */
export function storeConsent(consentData) {
  // Generate and store TC String
  const tcString = encodeTCString(consentData);
  setCookie(TC_STRING_COOKIE, tcString);
  
  // Google AC providers get consent only when purpose 1 (store/access) is consented
  setCookie(AC_STRING_COOKIE, buildACString(!!consentData.purposeConsents?.has(1)));
  
  // Also store the raw consent data for easier retrieval
  const dataToStore = {
    purposeConsents: Array.from(consentData.purposeConsents || []),
    purposeLegitimateInterests: Array.from(consentData.purposeLegitimateInterests || []),
    vendorConsents: Array.from(consentData.vendorConsents || []),
    vendorLegitimateInterests: Array.from(consentData.vendorLegitimateInterests || []),
    specialFeatureOptins: Array.from(consentData.specialFeatureOptins || []),
    created: new Date().toISOString(),
    lastUpdated: new Date().toISOString()
  };
  
  setCookie(CONSENT_DATA_COOKIE, JSON.stringify(dataToStore));
  
  return tcString;
}

/**
 * Retrieve stored consent data
 * @returns {Object|null} - The stored consent data or null if not found
 */
export function getStoredConsent() {
  const tcString = getCookie(TC_STRING_COOKIE);
  const consentDataJson = getCookie(CONSENT_DATA_COOKIE);
  
  if (!tcString) {
    return null;
  }
  
  // Try to get the full consent data
  if (consentDataJson) {
    try {
      const parsed = JSON.parse(consentDataJson);
      return {
        tcString,
        purposeConsents: new Set(parsed.purposeConsents || []),
        purposeLegitimateInterests: new Set(parsed.purposeLegitimateInterests || []),
        vendorConsents: new Set(parsed.vendorConsents || []),
        vendorLegitimateInterests: new Set(parsed.vendorLegitimateInterests || []),
        specialFeatureOptins: new Set(parsed.specialFeatureOptins || []),
        created: parsed.created ? new Date(parsed.created) : new Date(),
        lastUpdated: parsed.lastUpdated ? new Date(parsed.lastUpdated) : new Date()
      };
    } catch (e) {
      console.error('Error parsing consent data cookie:', e);
    }
  }
  
  // Fall back to decoding the TC String
  const decoded = decodeTCString(tcString);
  if (decoded) {
    return {
      tcString,
      ...decoded
    };
  }
  
  return null;
}

/**
 * Get the stored TC String
 * @returns {string|null} - The TC String or null if not found
 */
export function getTCString() {
  return getCookie(TC_STRING_COOKIE);
}

/**
 * Get the stored Google Additional Consent string
 * @returns {string|null} - The AC String or null if not found
 */
export function getACString() {
  return getCookie(AC_STRING_COOKIE);
}

/**
 * Check if consent has been given
 * @returns {boolean} - Whether consent data exists
 */
export function hasConsent() {
  return !!getCookie(TC_STRING_COOKIE);
}

/**
 * Clear all consent data
 */
export function clearConsent() {
  deleteCookie(TC_STRING_COOKIE);
  deleteCookie(CONSENT_DATA_COOKIE);
  deleteCookie(AC_STRING_COOKIE);
}

/**
 * Google Analytics cookie names to clear on reject
 */
const GA_COOKIES = [
  '_ga',           // Main GA cookie
  '_gid',          // 24-hour tracking cookie
  '_gat',          // Throttle request rate
  '_gat_gtag',     // Google Analytics gtag
  '_ga_*',         // GA4 measurement ID cookies
  '__utma',        // Legacy GA
  '__utmb',        // Legacy GA
  '__utmc',        // Legacy GA
  '__utmt',        // Legacy GA
  '__utmz',        // Legacy GA
  '_gcl_au',       // Google Ads conversion linker
  '_gac_*',        // Google Ads conversion
];

/**
 * Clear tracking cookies (Google Analytics, etc.)
 * Called when user rejects consent
 */
export function clearTrackingCookies() {
  const cookies = document.cookie.split(';');
  
  cookies.forEach(cookie => {
    const cookieName = cookie.split('=')[0].trim();
    
    // Check if this cookie matches any GA pattern
    const shouldDelete = GA_COOKIES.some(pattern => {
      if (pattern.endsWith('*')) {
        return cookieName.startsWith(pattern.slice(0, -1));
      }
      return cookieName === pattern;
    });
    
    if (shouldDelete) {
      // Delete cookie on current domain and all parent domains
      const domain = window.location.hostname;
      const domains = [domain];
      
      // Add parent domains (e.g., .example.com for sub.example.com)
      const parts = domain.split('.');
      for (let i = 1; i < parts.length; i++) {
        domains.push('.' + parts.slice(i).join('.'));
      }
      
      domains.forEach(d => {
        document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${d}`;
        document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      });
      
      console.log(`Cleared tracking cookie: ${cookieName}`);
    }
  });
}

/**
 * Store consent with "accept all" defaults
 * @param {Array} purposeIds - All purpose IDs to consent to
 * @param {Array} vendorIds - All vendor IDs to consent to
 * @param {Array} specialFeatureIds - All special feature IDs to opt into
 */
export function storeAcceptAll(purposeIds, vendorIds, specialFeatureIds = []) {
  // Filter out deleted vendors - TCF requires deleted vendor signals to be 0
  const activeVendorIds = vendorIds.filter(id => !isVendorDeleted(id));
  
  const consentData = {
    purposeConsents: new Set(purposeIds),
    purposeLegitimateInterests: new Set(purposeIds.filter(id => [2, 7, 8, 9, 10, 11].includes(id))),
    vendorConsents: new Set(activeVendorIds),
    vendorLegitimateInterests: new Set(activeVendorIds),
    specialFeatureOptins: new Set(specialFeatureIds)
  };
  
  // Enable Google Analytics (sets consent mode to granted)
  enableGoogleAnalytics();
  
  return storeConsent(consentData);
}

/**
 * Store consent with "reject all" defaults
 * Only essential/required purposes are kept
 * Also deletes tracking cookies
 */
export function storeRejectAll() {
  const consentData = {
    purposeConsents: new Set(),
    purposeLegitimateInterests: new Set(),
    vendorConsents: new Set(),
    vendorLegitimateInterests: new Set(),
    specialFeatureOptins: new Set()
  };
  
  // Disable Google Analytics (sets consent mode to denied)
  disableGoogleAnalytics();
  
  // Delete all tracking cookies from configured vendors
  deleteAllTrackingCookies();
  
  return storeConsent(consentData);
}



